import { formatCharge, formatQuantity } from '../../../../utils/formatter';
import {
  getQuantityInCart,
  getStartingTotalPriceForGooglePLA,
  getWhetherShowStartingTotalPriceForGooglePLA,
} from '../../../../bom/selectors';

import Col from 'react-bootstrap/Col';
import React from 'react';
import Row from 'react-bootstrap/Row';
import SpinnerWrapper from '../../../common/SpinnerWrapper';
import TooltipWrapper from '../../../common/TooltipWrapper';
import { getMinimumQuantity } from '../../../../price/selectors';
import styles from './OrderSummary.scss';
import useOrderSummaryDetails from '../../../../ordersummary/hooks/useOrderSummaryDetails';
import { useSelector } from 'react-redux';

const OrderSummary = () => {
  const details = useOrderSummaryDetails();
  const { chargeDetails, discountInfo, totalChargeInfo, validation } = details;
  const { essentialCharge, freeQuantity, specialCharges } = chargeDetails;

  const quantityInCart = useSelector(getQuantityInCart);
  const miniQty = useSelector(getMinimumQuantity);
  const showStartingTotalPriceForGooglePLA = useSelector(getWhetherShowStartingTotalPriceForGooglePLA);
  const startingTotalPriceForGooglePLA = useSelector(getStartingTotalPriceForGooglePLA);

  const itemInCart = quantityInCart > 0;
  const showUnitPriceDescriptionIcon = essentialCharge.hasDiscount || itemInCart;
  const messageBasedOnQuantityInCart = `*Unit Price reflects total quantity of ${formatQuantity(
    essentialCharge.quantity + quantityInCart
  )}, based on the quantity already in cart.`;
  const messageBasedOnCouponApplied = `${
    itemInCart ? 'It also' : '*Unit Price'
  } reflects the discounted price based on the applied order level coupon.`;
  const unitPriceTooltipMessage =
    itemInCart && essentialCharge.hasDiscount
      ? `${messageBasedOnQuantityInCart} ${messageBasedOnCouponApplied}`
      : itemInCart
      ? messageBasedOnQuantityInCart
      : essentialCharge.hasDiscount
      ? messageBasedOnCouponApplied
      : '';

  return (
    <Row className={styles['order-summary']}>
      <Col className={styles['charges']}>
        <ul>
          {itemInCart && (
            <li key="quantity-in-cart">
              <span className={`${styles['icon']} ${styles['cart']}`} />
              <span>{`${formatQuantity(quantityInCart)} in the cart`}</span>
            </li>
          )}
          {!validation.isValid ? (
            <li key="minimum-quantity">
              <span>
                {'Add Minimum Quantity of '}
                <b className={styles['mini-qty']}>{formatQuantity(miniQty)}</b>
              </span>
            </li>
          ) : (
            <>
              <li key="essential-charge">
                {itemInCart && (
                  <>
                    <span className={`${styles['icon']} ${styles['checkmark']}`} />
                    <span className={styles['qty']}>{`${formatQuantity(essentialCharge.quantity)} more selected`}</span>
                  </>
                )}
                <span>
                  {`${itemInCart ? '' : `${formatQuantity(essentialCharge.quantity)} `}starting at `}
                  {essentialCharge.isCalculating ? (
                    <SpinnerWrapper as="span" meaning="Calculating Unit Price" size="sm" />
                  ) : (
                    <b className={essentialCharge.hasDiscount ? styles['sale'] : undefined}>
                      {formatCharge(essentialCharge.unitPrice)}
                    </b>
                  )}
                  {` / unit${showUnitPriceDescriptionIcon ? '*' : ''}`}
                </span>
                {showUnitPriceDescriptionIcon && (
                  <TooltipWrapper
                    id="unit-price-description-tooltip"
                    message={unitPriceTooltipMessage}
                    trigger={['focus', 'hover']}>
                    <span className={`${styles['icon']} ${styles['info']}`} tabIndex="0" />
                  </TooltipWrapper>
                )}
                {freeQuantity > 0 && (
                  <span className={styles['free-qty']}>{`+${formatQuantity(freeQuantity)} FREE`}</span>
                )}
              </li>
              {specialCharges.map((specialCharge, index) => {
                const key = `special-charge-${index.toString()}`;
                const { chargeName, chargeDescription, total } = specialCharge;
                return (
                  <li key={key}>
                    <span>{chargeName}</span>
                    {!!chargeDescription && (
                      <TooltipWrapper id={`${key}-tooltip`} message={chargeDescription} trigger={['focus', 'hover']}>
                        <span className={`${styles['icon']} ${styles['info']}`} tabIndex="0" />
                      </TooltipWrapper>
                    )}
                    <span>{`+${formatCharge(total)}`}</span>
                  </li>
                );
              })}
            </>
          )}
        </ul>
      </Col>
      <Col className={styles['subtotal-details']} xs="auto">
        {!validation.isValid ? (
          <span className={styles['subtotal']}>
            {formatCharge(showStartingTotalPriceForGooglePLA ? startingTotalPriceForGooglePLA : 0.0)}
          </span>
        ) : totalChargeInfo.isCalculating ? (
          <SpinnerWrapper meaning="Calculating Subtotal" meaningVisible />
        ) : discountInfo.discount > 0 ? (
          <>
            <div className={styles['subtotal-block']}>
              <span className={styles['regular-subtotal']}>
                {formatCharge(totalChargeInfo.totalCharge + discountInfo.discount)}
              </span>
              <span className={styles['subtotal']}>{formatCharge(totalChargeInfo.totalCharge)}</span>
            </div>
            <div className={styles['savings-block']}>
              <span className={`${styles['icon']} ${styles['savings']}`} />
              <span className={styles['savings']}>{`Savings: ${formatCharge(discountInfo.discount)}`}</span>
            </div>
          </>
        ) : (
          <span className={styles['subtotal']}>{formatCharge(totalChargeInfo.totalCharge)}</span>
        )}
      </Col>
    </Row>
  );
};

export default OrderSummary;
